import NextImage, { type ImageProps } from 'next/image';

const CONTENTFUL_SRCSET_WIDTHS = [320, 640, 750, 828, 1080, 1200, 1920, 2560];
const CONTENTFUL_HOST = 'images.ctfassets.net';

function isContentfulUrl(src: string): boolean {
	try {
		return new URL(src).hostname === CONTENTFUL_HOST;
	} catch {
		return false;
	}
}

function buildContentfulUrl(
	src: string,
	width: number,
	height: number | undefined,
	quality: number,
	focus: string | undefined
): string {
	const url = new URL(src);
	url.searchParams.set('fit', 'fill');
	url.searchParams.set('w', String(width));
	if (height !== undefined) {
		url.searchParams.set('h', String(height));
	}
	url.searchParams.set('q', String(quality));
	url.searchParams.set('fm', 'webp');
	if (focus) {
		url.searchParams.set('f', focus);
	}
	return url.toString();
}

function buildContentfulSrcset(
	src: string,
	maxWidth: number,
	maxHeight: number | undefined,
	quality: number,
	focus: string | undefined
): string {
	const widths = CONTENTFUL_SRCSET_WIDTHS.filter((w) => w < maxWidth);
	widths.push(Math.min(maxWidth, 4000)); // Contentful's max width is 4000px
	const h = (w: number) =>
		maxHeight !== undefined
			? Math.floor((w * maxHeight) / maxWidth)
			: undefined;
	return widths
		.map((w) => `${buildContentfulUrl(src, w, h(w), quality, focus)} ${w}w`)
		.join(', ');
}

/**
 * Throws early when src is null so the error is easier to debug.
 * For Contentful images, uses the Contentful Image API with srcset for
 * client-side responsive resizing instead of Next.js server-side optimization.
 * Falls back to NextImage for static imports, fill mode, or non-Contentful URLs.
 */
type Props = ImageProps & { focus?: string };

export const Image: React.FC<Props> = ({
	alt,
	src,
	quality = 95,
	width,
	height,
	sizes,
	priority,
	fill,
	focus,
	...props
}) => {
	if (src == null) {
		throw new Error('Image src is required');
	}

	if (typeof src === 'string' && src.startsWith('//')) {
		src = 'https:' + src;
	}

	if (height !== undefined && typeof height !== 'number') {
		height = Number(height);
	}

	if (
		typeof src === 'string' &&
		!fill &&
		typeof width === 'number' &&
		isContentfulUrl(src) &&
		!src.endsWith('.svg')
	) {
		const q = typeof quality === 'number' ? quality : 95;
		return (
			<img
				{...props}
				alt={alt}
				src={buildContentfulUrl(src, width, height, q, focus)}
				srcSet={buildContentfulSrcset(src, width, height, q, focus)}
				sizes={sizes}
				width={width}
				loading={priority ? 'eager' : 'lazy'}
				decoding="async"
			/>
		);
	}

	return (
		<NextImage
			alt={alt}
			src={src}
			quality={quality}
			width={width}
			height={height}
			sizes={sizes}
			priority={priority}
			fill={fill}
			{...props}
		/>
	);
};
